// Domain/mapping layer over workersHttpClient. Mirrors organisationApi: maps DTOs to view
// models, builds pagination params, caches reference data per session, and converts the
// `ApiResponse` envelope into UI-friendly results that surface backend business-rule messages
// (BR-CT-001 last email, BR-AD-001 residential retention, terminated-person 409).

import {
  createAddress,
  createContact,
  createPerson,
  deleteAddress,
  deleteContact,
  fetchAddresses,
  fetchAddressTypes,
  fetchContacts,
  fetchContactTypes,
  fetchEmergencyContacts,
  fetchGenders,
  fetchPersonById,
  fetchPersons,
  fetchTitles,
  updateAddress,
  updateContact,
  updatePersonDetail,
} from './workersHttpClient';
import {
  ADDRESS_TYPE_VALUES,
  CONTACT_TYPE_VALUES,
  GENDER_VALUES,
  TITLE_VALUES,
  type ApiResponse,
  type CreatePersonAddressRequest,
  type CreatePersonContactRequest,
  type CreatePersonRequest,
  type PersonAddressResponse,
  type PersonContactResponse,
  type PersonDetailResponse,
  type PersonEmergencyContactResponse,
  type PersonListParams,
  type PersonStatus,
  type UpdatePersonAddressRequest,
  type UpdatePersonContactRequest,
  type UpdatePersonDetailRequest,
} from './workersApiTypes';

/** A row in the Workers list (Person-layer columns only — see plan OQ-3). */
export interface WorkerListItem {
  personId: string;
  legalName: string;
  preferredName?: string;
  globalEmployeeId?: string;
  status: PersonStatus;
  dateOfBirth?: string;
}

/** Result of a read that returns a list. */
export interface ListResult<T> {
  ok: boolean;
  items: T[];
  message?: string;
}

/** Result of a mutation; `message` carries backend business-rule text on failure. */
export interface MutationResult<T = unknown> {
  ok: boolean;
  data?: T;
  message?: string;
  statusCode?: number;
}

const DEFAULT_ERROR = 'Something went wrong. Please try again.';

const toMutationResult = <T>(response: ApiResponse<T>): MutationResult<T> => ({
  ok: response.success,
  data: response.result,
  message: response.success ? undefined : response.message ?? DEFAULT_ERROR,
  statusCode: response.statusCode,
});

export const fullLegalName = (person: PersonDetailResponse): string =>
  [person.legalFirstName, person.legalMiddleName, person.legalLastName].filter(Boolean).join(' ').trim() || '—';

const mapPersonToListItem = (person: PersonDetailResponse): WorkerListItem => ({
  personId: person.personId ?? '',
  legalName: fullLegalName(person),
  preferredName: person.preferredFirstName ?? undefined,
  globalEmployeeId: person.globalEmployeeId ?? undefined,
  status: person.status ?? 'ACTIVE',
  dateOfBirth: person.dateOfBirth ?? undefined,
});

// --- Person ---

export const listWorkers = async (params: PersonListParams = {}): Promise<ListResult<WorkerListItem>> => {
  const response = await fetchPersons({ take: 20, skip: 0, ...params });
  return {
    ok: response.success,
    items: (response.result ?? []).filter((p) => p.personId).map(mapPersonToListItem),
    message: response.success ? undefined : response.message ?? DEFAULT_ERROR,
  };
};

export const getWorker = async (personId: string, effectiveDate?: string): Promise<MutationResult<PersonDetailResponse>> =>
  toMutationResult(await fetchPersonById(personId, effectiveDate));

export const createWorker = async (request: CreatePersonRequest): Promise<MutationResult<PersonDetailResponse>> =>
  toMutationResult(await createPerson(request));

export const updateWorkerDetail = async (personId: string, request: UpdatePersonDetailRequest): Promise<MutationResult<PersonDetailResponse>> =>
  toMutationResult(await updatePersonDetail(personId, request));

// --- Contacts ---

export const listContacts = async (personId: string): Promise<ListResult<PersonContactResponse>> => {
  const response = await fetchContacts(personId);
  return { ok: response.success, items: response.result ?? [], message: response.success ? undefined : response.message ?? DEFAULT_ERROR };
};

export const addContact = async (personId: string, request: CreatePersonContactRequest): Promise<MutationResult<PersonContactResponse>> =>
  toMutationResult(await createContact(personId, request));

export const editContact = async (personId: string, contactId: string, request: UpdatePersonContactRequest): Promise<MutationResult<PersonContactResponse>> =>
  toMutationResult(await updateContact(personId, contactId, request));

export const removeContact = async (personId: string, contactId: string): Promise<MutationResult<PersonContactResponse>> =>
  toMutationResult(await deleteContact(personId, contactId));

// --- Addresses ---

export const listAddresses = async (personId: string): Promise<ListResult<PersonAddressResponse>> => {
  const response = await fetchAddresses(personId);
  return { ok: response.success, items: response.result ?? [], message: response.success ? undefined : response.message ?? DEFAULT_ERROR };
};

export const addAddress = async (personId: string, request: CreatePersonAddressRequest): Promise<MutationResult<PersonAddressResponse>> =>
  toMutationResult(await createAddress(personId, request));

export const editAddress = async (personId: string, addressId: string, request: UpdatePersonAddressRequest): Promise<MutationResult<PersonAddressResponse>> =>
  toMutationResult(await updateAddress(personId, addressId, request));

export const removeAddress = async (personId: string, addressId: string): Promise<MutationResult<PersonAddressResponse>> =>
  toMutationResult(await deleteAddress(personId, addressId));

// --- Emergency contacts (read-only) ---

export const listEmergencyContacts = async (personId: string): Promise<ListResult<PersonEmergencyContactResponse>> => {
  const response = await fetchEmergencyContacts(personId);
  const items = (response.result ?? []).slice().sort((a, b) => (a.priorityOrder ?? 0) - (b.priorityOrder ?? 0));
  return { ok: response.success, items, message: response.success ? undefined : response.message ?? DEFAULT_ERROR };
};

// --- Reference data (cached per session; falls back to compiled-in constants) ---

export interface ReferenceData {
  titles: string[];
  genders: string[];
  contactTypes: string[];
  addressTypes: string[];
}

let referenceCache: ReferenceData | null = null;
let referenceInFlight: Promise<ReferenceData> | null = null;

const resolveRef = (response: ApiResponse<string[]>, fallback: readonly string[]): string[] =>
  response.success && response.result?.length ? response.result : [...fallback];

// Never reject: a network throw from authFetch becomes a failed envelope so resolveRef falls back
// to the compiled-in constants (otherwise every dropdown would be empty when offline).
const safeRefFetch = async (fn: () => Promise<ApiResponse<string[]>>): Promise<ApiResponse<string[]>> => {
  try {
    return await fn();
  } catch {
    return { success: false, statusCode: 0 };
  }
};

export const getReferenceData = async (): Promise<ReferenceData> => {
  if (referenceCache) return referenceCache;
  // De-dupe concurrent callers (e.g. PersonalTab + WorkerFormPage mounting together) so the
  // four reference GETs fire only once per session.
  if (referenceInFlight) return referenceInFlight;
  referenceInFlight = (async () => {
    const [titles, genders, contactTypes, addressTypes] = await Promise.all([
      safeRefFetch(fetchTitles),
      safeRefFetch(fetchGenders),
      safeRefFetch(fetchContactTypes),
      safeRefFetch(fetchAddressTypes),
    ]);
    referenceCache = {
      titles: resolveRef(titles, TITLE_VALUES),
      genders: resolveRef(genders, GENDER_VALUES),
      contactTypes: resolveRef(contactTypes, CONTACT_TYPE_VALUES),
      addressTypes: resolveRef(addressTypes, ADDRESS_TYPE_VALUES),
    };
    return referenceCache;
  })();
  try {
    return await referenceInFlight;
  } finally {
    referenceInFlight = null;
  }
};

/** Test/dev helper to reset the per-session reference cache. */
export const resetReferenceCache = (): void => {
  referenceCache = null;
  referenceInFlight = null;
};
